/**
 * 色相/饱和度对话框（Ctrl+U，参照 Photoshop「图像→调整→色相/饱和度」，
 * 破坏性）。三个滑杆，实时预览，确定后一次性改活动图层像素。
 */
import type { JSX } from 'solid-js';
import { Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import type { HslParams } from '../../engine/adjustments/lut';
import styles from './HueSaturationDialog.module.css';

export const DEFAULT_HUE_SATURATION_PARAMS: HslParams = { hue: 0, saturation: 0, lightness: 0 };

export interface HueSaturationDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onApply: (params: HslParams) => void;
  readonly previewUrl?: string | undefined;
  readonly onPreview?: ((params: HslParams) => void) | undefined;
}

export function HueSaturationDialog(props: HueSaturationDialogProps): JSX.Element {
  const [hue, setHue] = createSignal(DEFAULT_HUE_SATURATION_PARAMS.hue);
  const [saturation, setSaturation] = createSignal(DEFAULT_HUE_SATURATION_PARAMS.saturation);
  const [lightness, setLightness] = createSignal(DEFAULT_HUE_SATURATION_PARAMS.lightness);
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  createEffect(() => {
    if (props.open) {
      setHue(DEFAULT_HUE_SATURATION_PARAMS.hue);
      setSaturation(DEFAULT_HUE_SATURATION_PARAMS.saturation);
      setLightness(DEFAULT_HUE_SATURATION_PARAMS.lightness);
    }
  });

  const params = (): HslParams => ({ hue: hue(), saturation: saturation(), lightness: lightness() });

  createEffect(() => {
    if (!props.open) return;
    props.onPreview?.(params());
  });

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} onClick={props.onClose} data-testid="hue-saturation-backdrop">
        <div
          class={styles.dialog}
          role="dialog"
          ref={(el) => (dialogRef = el)}
          aria-label="色相/饱和度"
          data-testid="hue-saturation-dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 class={styles.title}>色相/饱和度</h2>

          <Show when={props.previewUrl !== undefined}>
            <div class={styles.previewPanel}>
              <img
                class={styles.previewImage}
                src={props.previewUrl}
                alt="色相/饱和度预览"
                data-testid="hue-saturation-preview"
              />
            </div>
          </Show>

          <label class={styles.row}>
            <span class={styles.label}>色相</span>
            <input
              type="range"
              min="-180"
              max="180"
              value={hue()}
              data-testid="hue-saturation-hue"
              onInput={(e) => setHue(Number(e.currentTarget.value))}
            />
            <span class={styles.value}>{hue()}</span>
          </label>
          <label class={styles.row}>
            <span class={styles.label}>饱和度</span>
            <input
              type="range"
              min="-100"
              max="100"
              value={saturation()}
              data-testid="hue-saturation-saturation"
              onInput={(e) => setSaturation(Number(e.currentTarget.value))}
            />
            <span class={styles.value}>{saturation()}</span>
          </label>
          <label class={styles.row}>
            <span class={styles.label}>明度</span>
            <input
              type="range"
              min="-100"
              max="100"
              value={lightness()}
              data-testid="hue-saturation-lightness"
              onInput={(e) => setLightness(Number(e.currentTarget.value))}
            />
            <span class={styles.value}>{lightness()}</span>
          </label>

          <div class={styles.actions}>
            <button type="button" class={styles.secondary} onClick={props.onClose}>
              取消
            </button>
            <button
              type="button"
              class={styles.primary}
              data-testid="hue-saturation-confirm"
              onClick={() => props.onApply(params())}
            >
              确定
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
